import React, { createContext, useContext, useState, useEffect } from "react";
import { toast } from "react-toastify";
import { walletService } from "../services";

// Création du contexte
const WalletContext = createContext();

// Hook personnalisé pour utiliser le contexte
export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error(
      "useWallet doit être utilisé à l'intérieur d'un WalletProvider",
    );
  }
  return context;
};

// Provider du contexte
export const WalletProvider = ({ children }) => {
  // État initial du wallet
  const [wallet, setWallet] = useState({
    connected: false,
    address: "",
    balance: {
      BBT: 0,
      USDT: 0,
      ETH: 0,
      XOF: 0,
    },
    network: "",
    provider: null,
    connecting: false,
    error: null,
  });
  const [walletInitialized, setWalletInitialized] = useState(false);

  // Charger l'état du wallet depuis le localStorage lors du chargement
  useEffect(() => {
    const savedWallet = localStorage.getItem("wallet");
    if (savedWallet) {
      try {
        const parsedWallet = JSON.parse(savedWallet);
        // Ne restaurer que si les données sont valides
        if (parsedWallet && parsedWallet.address) {
          setWallet((prev) => ({
            ...prev,
            ...parsedWallet,
            // Réinitialiser l'état de connexion car nous devons reconnecter
            connecting: false,
            provider: null,
            error: null,
          }));

          // Tenter de reconnecter automatiquement
          reconnectWallet(parsedWallet.address);
        }
      } catch (error) {
        console.error("Erreur lors du chargement du wallet:", error);
        localStorage.removeItem("wallet");
      }
    }
  }, []);

  // Sauvegarder l'état du wallet dans localStorage quand il change
  useEffect(() => {
    if (wallet.connected && wallet.address) {
      const walletData = {
        connected: wallet.connected,
        address: wallet.address,
        balance: wallet.balance,
        network: wallet.network,
      };
      localStorage.setItem("wallet", JSON.stringify(walletData));
    }
  }, [wallet.connected, wallet.address, wallet.balance, wallet.network]);

  // Fonction pour se connecter au wallet
  const connectWallet = async () => {
    try {
      setWallet((prev) => ({ ...prev, connecting: true, error: null }));

      // Vérifier si window.ethereum est disponible
      if (!window.ethereum) {
        throw new Error(
          "Veuillez installer un wallet compatible (ex: MetaMask)",
        );
      }

      // Demander la connexion
      const accounts = await window.ethereum.request({
        method: "eth_requestAccounts",
      });

      if (!accounts || accounts.length === 0) {
        throw new Error("Aucun compte détecté");
      }

      // Récupérer le réseau actuel
      const chainId = await window.ethereum.request({ method: "eth_chainId" });
      const networkName = getNetworkName(chainId);

      // Récupérer le solde ETH
      const balance = await window.ethereum.request({
        method: "eth_getBalance",
        params: [accounts[0], "latest"],
      });

      // Convertir le solde de wei en ETH
      const ethBalance = parseInt(balance, 16) / Math.pow(10, 18);

      // Mettre à jour l'état
      setWallet((prev) => ({
        ...prev,
        connected: true,
        address: accounts[0],
        balance: {
          ...prev.balance,
          ETH: ethBalance,
        },
        network: networkName,
        provider: window.ethereum,
        connecting: false,
        error: null,
      }));

      // Charger les soldes fictifs pour les autres tokens (à remplacer par des appels API réels)
      loadTokenBalances(accounts[0]);

      toast.success("Wallet connecté avec succès");

      // Ajouter des écouteurs d'événements pour les changements
      setupEventListeners();
    } catch (error) {
      console.error("Erreur de connexion au wallet:", error);
      setWallet((prev) => ({
        ...prev,
        connected: false,
        connecting: false,
        error: error.message || "Erreur lors de la connexion au wallet",
      }));
      toast.error(error.message || "Erreur lors de la connexion au wallet");
    }
  };

  // Fonction pour reconnecter automatiquement
  const reconnectWallet = async (address) => {
    try {
      // Vérifier si window.ethereum est disponible
      if (!window.ethereum) {
        return; // Silencieusement échouer pour la reconnexion automatique
      }

      // Vérifier si l'adresse est toujours disponible
      const accounts = await window.ethereum.request({
        method: "eth_accounts",
      });

      if (!accounts || !accounts.includes(address)) {
        throw new Error("Adresse non disponible");
      }

      // Récupérer le réseau actuel
      const chainId = await window.ethereum.request({ method: "eth_chainId" });
      const networkName = getNetworkName(chainId);

      // Récupérer le solde ETH
      const balance = await window.ethereum.request({
        method: "eth_getBalance",
        params: [address, "latest"],
      });

      // Convertir le solde de wei en ETH
      const ethBalance = parseInt(balance, 16) / Math.pow(10, 18);

      // Mettre à jour l'état
      setWallet((prev) => ({
        ...prev,
        connected: true,
        address: address,
        balance: {
          ...prev.balance,
          ETH: ethBalance,
        },
        network: networkName,
        provider: window.ethereum,
        connecting: false,
        error: null,
      }));

      // Charger les soldes fictifs pour les autres tokens
      loadTokenBalances(address);

      // Ajouter des écouteurs d'événements pour les changements
      setupEventListeners();
    } catch (error) {
      console.error("Erreur de reconnexion au wallet:", error);
      setWallet((prev) => ({
        ...prev,
        connected: false,
        address: "",
        connecting: false,
        error: null, // Ne pas afficher d'erreur pour une reconnexion silencieuse
      }));
      localStorage.removeItem("wallet");
    }
  };

  // Fonction pour se déconnecter du wallet
  const disconnectWallet = () => {
    // Supprimer les écouteurs d'événements
    if (window.ethereum) {
      window.ethereum.removeListener("accountsChanged", handleAccountsChanged);
      window.ethereum.removeListener("chainChanged", handleChainChanged);
      window.ethereum.removeListener("disconnect", handleDisconnect);
    }

    // Réinitialiser l'état
    setWallet({
      connected: false,
      address: "",
      balance: {
        BBT: 0,
        USDT: 0,
        ETH: 0,
        XOF: 0,
      },
      network: "",
      provider: null,
      connecting: false,
      error: null,
    });

    // Supprimer du localStorage
    localStorage.removeItem("wallet");

    toast.success("Wallet déconnecté");
  };

  // Fonction pour charger les soldes des tokens (simulée)
  const loadTokenBalances = async (address) => {
    try {
      // Cette fonction devrait être remplacée par des appels réels aux contrats ERC20 ou à l'API
      setWallet((prev) => ({
        ...prev,
        balance: {
          ...prev.balance,
          BBT: Math.random() * 1000, // Valeurs aléatoires à titre d'exemple
          USDT: Math.random() * 500,
          XOF: Math.random() * 50000,
        },
      }));
    } catch (error) {
      console.error("Erreur lors du chargement des soldes:", error);
    }
  };

  // Configurer les écouteurs d'événements
  const setupEventListeners = () => {
    if (!window.ethereum) return;

    // Écouter les changements de compte
    window.ethereum.on("accountsChanged", handleAccountsChanged);

    // Écouter les changements de réseau
    window.ethereum.on("chainChanged", handleChainChanged);

    // Écouter les déconnexions
    window.ethereum.on("disconnect", handleDisconnect);
  };

  // Gérer les changements de compte
  const handleAccountsChanged = (accounts) => {
    if (!accounts.length) {
      // L'utilisateur a déconnecté tous les comptes
      disconnectWallet();
      toast.info("Wallet déconnecté");
    } else if (accounts[0] !== wallet.address) {
      // L'utilisateur a changé de compte
      setWallet((prev) => ({
        ...prev,
        address: accounts[0],
      }));
      loadTokenBalances(accounts[0]);
      toast.info(
        "Compte changé: " +
          accounts[0].slice(0, 6) +
          "..." +
          accounts[0].slice(-4),
      );
    }
  };

  // Gérer les changements de réseau
  const handleChainChanged = (chainId) => {
    const networkName = getNetworkName(chainId);
    setWallet((prev) => ({
      ...prev,
      network: networkName,
    }));
    toast.info("Réseau changé: " + networkName);
  };

  // Gérer les déconnexions
  const handleDisconnect = (error) => {
    disconnectWallet();
    if (error) {
      console.error("Erreur de déconnexion:", error);
    }
  };

  // Obtenir le nom du réseau à partir de l'ID de chaîne
  const getNetworkName = (chainId) => {
    const networks = {
      "0x1": "Ethereum Mainnet",
      "0x3": "Ropsten Testnet",
      "0x4": "Rinkeby Testnet",
      "0x5": "Goerli Testnet",
      "0x2a": "Kovan Testnet",
      "0x38": "Binance Smart Chain",
      "0x89": "Polygon",
      "0xa86a": "Avalanche",
    };

    return networks[chainId] || "Réseau inconnu";
  };

  // Fonction pour créer ou initialiser le portefeuille d'un utilisateur
  const initializeUserWallet = async (userId, currency = "RUB") => {
    if (walletInitialized || !userId) return;

    let attempts = 0;
    const maxAttempts = 3;

    while (attempts < maxAttempts) {
      try {
        const data = await walletService.initializeWallet(userId, currency);

        // Mettre à jour l'état du wallet avec les nouvelles données
        if (data && data.wallet) {
          setWallet((prev) => ({
            ...prev,
            balance: {
              ...prev.balance,
              [currency]: data.wallet.balance || 0,
            },
          }));
          setWalletInitialized(true);
        }

        return data;
      } catch (error) {
        attempts++;
        console.error(
          "Erreur lors de l'initialisation du portefeuille:",
          error,
        );

        // Si c'est une erreur 429 (trop de requêtes), attendre avant de réessayer
        if (error.response?.status === 429 && attempts < maxAttempts) {
          const delay = 1000 * Math.pow(2, attempts);
          console.log(`Nouvelle tentative dans ${delay / 1000} secondes...`);
          await new Promise((resolve) => setTimeout(resolve, delay));
        } else {
          return null;
        }
      }
    }

    return null;
  };

  // Valeur du contexte à fournir
  const value = {
    wallet,
    connectWallet,
    disconnectWallet,
    loadTokenBalances,
    initializeUserWallet,
  };

  return (
    <WalletContext.Provider value={value}>{children}</WalletContext.Provider>
  );
};
